import {Button, Input, message, Space, Table, Tag, Tooltip} from 'antd';
import {CheckOutlined, ExclamationCircleOutlined, MergeCellsOutlined, SearchOutlined} from "@ant-design/icons";
import {NOT_AVAILABLE, OPERATING_INSTRUCTIONS, YYYY_MM_DD_HH_MM} from "../../../util/Constants";
import React, {useEffect, useRef, useState} from "react";
import adminService from "../../../service/adminService";
import {
    MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_ACCEPT_ERROR,
    MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_ACCEPT_SUCCESS,
    MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_MERGE_ERROR,
    MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_MERGED_SUCCESS,
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import modal from 'antd/es/modal';
import dayjs from "dayjs";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from 'react-highlight-words';
import {InputRef} from 'antd/es/input';
import {JointAccountTemplateAdminDto} from "../../../dto/admin/JointAccountTemplateAdminDto";
import CommonFunction from "../../../util/CommonFunction";

interface JointAccountTemplateRequestTableProps {
    tableData: JointAccountTemplateAdminDto[],
    tableUpdateFunc: (data: boolean) => void,
}

type DataIndex = keyof JointAccountTemplateAdminDto;
const JointAccountTemplateRequestTable = (props: JointAccountTemplateRequestTableProps) => {
    const [tableData, setTableData] = useState<JointAccountTemplateAdminDto[]>();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isTableLoading, setTableLoading] = useState<boolean>(false);
    const searchInput = useRef<InputRef>();

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    useEffect(() => {
        setTableData(props.tableData)
    }, [props.tableData])

    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns = [
        {
            title: 'No',
            dataIndex: 'key',
            render: (text: any, rec: any, index: number) => {
                return (<span>{index + 1}</span>)
            }
        },
        {
            title: 'Portfolio Number',
            dataIndex: 'portfolioNumber',
            key: 'portfolioNumber',
            sorter: (a: any, b: any) => {
                a = a.portfolioNumber == null ? '' : a.portfolioNumber
                b = b.portfolioNumber == null ? '' : b.portfolioNumber
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('portfolioNumber', 'Portfolio Number'),
            render: (text: any, rec: any) => {
                return <span>{CommonFunction.validateText(text)}</span>
            },
        },
        {
            title: 'Primary Holder CIN',
            dataIndex: 'primaryHolderCin',
            key: 'primaryHolderCin',
            sorter: (a: any, b: any) => {
                a = a.primaryHolderCin == null ? '' : a.primaryHolderCin
                b = b.primaryHolderCin == null ? '' : b.primaryHolderCin
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('primaryHolderCin', 'Primary Holder CIN'),
        },
        {
            title: 'Primary Holder Name',
            dataIndex: 'primaryHolderName',
            key: 'primaryHolderName',
            sorter: (a: any, b: any) => {
                a = a.primaryHolderName == null ? '' : a.primaryHolderName
                b = b.primaryHolderName == null ? '' : b.primaryHolderName
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('primaryHolderName', 'Primary Holder Name'),
        },
        {
            title: 'Primary Holder NIC',
            dataIndex: 'primaryHolderNic',
            key: 'primaryHolderNic',
            sorter: (a: any, b: any) => {
                a = a.primaryHolderNic == null ? '' : a.primaryHolderNic
                b = b.primaryHolderNic == null ? '' : b.primaryHolderNic
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('primaryHolderNic', 'Primary Holder NIC'),
        },
        {
            title: 'Secondary Holder CIN',
            dataIndex: 'secondaryHolderCin',
            key: 'secondaryHolderCin',
            sorter: (a: any, b: any) => {
                a = a.secondaryHolderCin == null ? '' : a.secondaryHolderCin
                b = b.secondaryHolderCin == null ? '' : b.secondaryHolderCin
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('secondaryHolderCin', 'Secondary Holder CIN'),
        },
        {
            title: 'Secondary Holder Name',
            dataIndex: 'secondaryHolderName',
            key: 'secondaryHolderName',
            sorter: (a: any, b: any) => {
                a = a.secondaryHolderName == null ? '' : a.secondaryHolderName
                b = b.secondaryHolderName == null ? '' : b.secondaryHolderName
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('secondaryHolderName', 'Secondary Holder Name'),
        },
        {
            title: 'Secondary Holder NIC',
            dataIndex: 'secondaryHolderNic',
            key: 'secondaryHolderNic',
            sorter: (a: any, b: any) => {
                a = a.secondaryHolderNic == null ? '' : a.secondaryHolderNic
                b = b.secondaryHolderNic == null ? '' : b.secondaryHolderNic
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('secondaryHolderNic', 'Secondary Holder NIC'),
        }, {
            title: 'Account Number',
            dataIndex: 'accountNumber',
            key: 'accountNumber',
            sorter: (a: any, b: any) => {
                a = a.accountNumber == null ? '' : a.accountNumber
                b = b.accountNumber == null ? '' : b.accountNumber
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('accountNumber', 'Account Number'),
            render: (text: any, rec: any) => {
                return <span>{CommonFunction.validateText(text)}</span>
            }
        },
        {
            title: 'Operating Instruction',
            dataIndex: 'operatingInstructions',
            align: "center",
            key: 'operatingInstructions',
            sorter: (a: any, b: any) => {
                a = a.operatingInstructions == null ? '' : a.operatingInstructions
                b = b.operatingInstructions == null ? '' : b.operatingInstructions
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('operatingInstructions', 'Operating Instruction'),
            render: (text: any, rec: any) => {
                return <span>{getOperatingInstructionTag(text)}</span>
            }
        },


        {
            title: 'Request Date Time',
            dataIndex: 'requestedDateTime',
            key: 'requestedDateTime',
            sorter: (a: any, b: any) => {
                a = a.requestedDateTime == null ? '' : a.requestedDateTime
                b = b.requestedDateTime == null ? '' : b.requestedDateTime
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },

        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (text: any, rec: JointAccountTemplateAdminDto) => (
                <Space size="middle">
                    {rec.adminApproved ?
                        <Tooltip placement="top" title='Merge'>
                            <Button className='btn-action-outline-common btn-action-assign' icon={<MergeCellsOutlined/>}
                                    onClick={() => mergeJointAccountTemplateRequest(rec)}>Merge</Button>
                        </Tooltip>
                        :
                        <Tooltip placement="top" title='Accept'>
                            <Button className='btn-action-outline-common btn-action-view' icon={<CheckOutlined/>}
                                    onClick={() => acceptJointAccountTemplateRequest(rec)}>Accept</Button>
                        </Tooltip>
                    }


                </Space>
            ),
        },
    ];


    const getOperatingInstructionTag = (value: string) => {
        switch (value) {
            case OPERATING_INSTRUCTIONS.EITHER_PARTIES_VALUE: {
                return (
                    <Tag
                        color="green" key={value}>
                        {OPERATING_INSTRUCTIONS.EITHER_PARTIES_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.BOTH_PARTIES_VALUE: {
                return (
                    <Tag
                        color="purple" key={value}>
                        {OPERATING_INSTRUCTIONS.BOTH_PARTIES_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.PRIMARY_PARTY_VALUE: {
                return (
                    <Tag
                        color="red" key={value}>
                        {OPERATING_INSTRUCTIONS.PRIMARY_PARTY_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.SECONDARY_PARTY_VALUE: {
                return (
                    <Tag
                        color="blue" key={value}>
                        {OPERATING_INSTRUCTIONS.SECONDARY_PARTY_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            default:{
                return (
                    <Tag
                        color="default" key={NOT_AVAILABLE}>
                        {NOT_AVAILABLE.toUpperCase()}
                    </Tag>
                )
            }
        }
    }

    const acceptJointAccountTemplateRequest = (record: JointAccountTemplateAdminDto) => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: `Do you want to accept this joint account template request?`,
            className: 'confirm-dialog-box',
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {
                setTableLoading(true)
                adminService.acceptJointAccountTemplateRequest(record.reference).then(res => {
                    if (res.data !== null && res.data.results) {
                        message.success(MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_ACCEPT_SUCCESS).then();
                        props.tableUpdateFunc(true);
                        setTableLoading(false)
                    } else {
                        message.error(MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_ACCEPT_ERROR).then();
                        setTableLoading(false)

                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    setTableLoading(false)
                })
            },
            onCancel() {
            }
        });
    }

    const mergeJointAccountTemplateRequest = (record: JointAccountTemplateAdminDto) => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: `Do you want to merge this joint account template request?`,
            className: 'confirm-dialog-box',
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {
                setTableLoading(true)
                adminService.mergeJointAccountTemplateRequest(record.reference).then(res => {
                    if (res.data !== null && res.data.results) {
                        message.success(MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_MERGED_SUCCESS).then();
                        props.tableUpdateFunc(true);
                        setTableLoading(false)
                    } else {
                        message.error(MSG_JOINT_ACCOUNT_TEMPLATE_REQUEST_MERGE_ERROR).then();
                        setTableLoading(false)

                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    setTableLoading(false)
                })
            },
            onCancel() {
            }
        });
    }
    return (
        <div>
            <Table scroll={{x: 'max-content'}} columns={columns} dataSource={tableData} loading={isTableLoading}/>
        </div>


    )
};


export default JointAccountTemplateRequestTable;
